import React, { useEffect, useRef } from 'react';

const ValueCard = ({ icon, title, description, index }) => {
  const cardRef = useRef(null);
  
  useEffect(() => {
    if (!cardRef.current) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add('animate-fadeInBottom');
          }, index * 200); // Kart sırasına göre gecikme ekle
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(cardRef.current);
    
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [index]);

  return (
    <div 
      ref={cardRef} 
      className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center text-center opacity-0 transform translate-y-8"
    >
      <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4 transition-all duration-500 hover:bg-indigo-200 hover:scale-110">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
      
      {/* Hover efekti için zarif bir çizgi */}
      <div className="w-0 group-hover:w-full h-0.5 bg-indigo-500 mt-4 transition-all duration-300"></div>
    </div>
  );
};

const AboutValues = () => {
  const sectionRef = useRef(null);
  
  useEffect(() => {
    if (!sectionRef.current) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.querySelector('h2').classList.add('animate-fadeInBottom');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(sectionRef.current);
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <div ref={sectionRef} className="bg-indigo-50 p-6 sm:p-10 overflow-hidden relative">
      {/* Arka plan deseni */}
      <div className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%" className="text-indigo-500">
          <defs>
            <pattern id="music-pattern" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
              <path d="M0,15 a7.5,7.5 0 1,1 15,0 a7.5,7.5 0 1,1 -15,0 M30,15 a7.5,7.5 0 1,1 15,0 a7.5,7.5 0 1,1 -15,0" strokeWidth="1" stroke="currentColor" fill="none"></path>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#music-pattern)"></rect>
        </svg>
      </div>
      
      <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center opacity-0 transform translate-y-4">
        Değerlerimiz
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
        <ValueCard 
          index={0}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
          }
          title="Müzikal Çeşitlilik"
          description="Her tür müziği kucaklıyor, farklı tarzları ve gelenekleri bir araya getiriyoruz. Klasikten rock'a, caz'dan elektronik müziğe her tarzı keşfediyoruz."
        />
        
        <ValueCard 
          index={1}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
          title="Kapsayıcılık"
          description="Müzik seviyesi veya deneyimi ne olursa olsun, tüm öğrencilere kapımız açık. Müzik yapmayı ve dinlemeyi seven herkes topluluğumuzun değerli bir parçasıdır."
        />
        
        <ValueCard 
          index={2}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
          title="Yenilikçilik"
          description="Müzik odası rezervasyon sistemimiz gibi yenilikçi çözümlerle müziğe erişimi kolaylaştırıyor, teknoloji ve müziği bir araya getiren projeler geliştiriyoruz."
        />
      </div>
      
      {/* Animasyon stilleri */}
      <style jsx>{`
        .animate-fadeInBottom {
          animation: fadeInBottom 0.8s ease forwards;
        }
        
        @keyframes fadeInBottom {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default AboutValues;